import { createPopper, type Placement } from '@popperjs/core'
import {
  forwardRef,
  isValidElement,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

type DropdownOffset = readonly [number, number?]

type DropdownProps = {
  ariaLabel?: string
  backdropZIndex?: number
  btnClassName?: string
  button: ReactNode
  children?: ReactNode
  offset?: DropdownOffset
  placement?: Placement
  title?: string
  zIndex?: number
}

export type DropdownHandle = {
  close: () => void
}

type NativeTriggerButtonProps = {
  'aria-label'?: string
  children?: ReactNode
  className?: string
  onClick?: (event: ReactMouseEvent<HTMLButtonElement>) => void
  title?: string
}

const Dropdown = (props: DropdownProps, forwardedRef: Ref<DropdownHandle>) => {
  const [visibility, setVisibility] = useState(false)
  const referenceRef = useRef<HTMLButtonElement | null>(null)
  const popperRef = useRef<HTMLDivElement | null>(null)
  const placement = props.placement ?? 'bottom-end'
  const offsetX = props.offset?.[0] ?? 0
  const offsetY = props.offset?.[1] ?? 0

  // Let Popper own positioning styles without its React adapter's flushSync
  // state updates. Closed dropdowns need no positioning or scroll listeners.
  useLayoutEffect(() => {
    const reference = referenceRef.current
    const popper = popperRef.current
    if (!visibility || !reference || !popper) return

    const instance = createPopper(reference, popper, {
      placement,
      modifiers: [{ name: 'offset', options: { offset: [offsetX, offsetY] } }],
    })
    instance.forceUpdate()
    const observer = new ResizeObserver(() => {
      void instance.update()
    })
    observer.observe(reference)
    observer.observe(popper)
    return () => {
      observer.disconnect()
      instance.destroy()
    }
  }, [visibility, placement, offsetX, offsetY])

  const toggleVisibility = () => {
    setVisibility((prevState) => !prevState)
  }

  const close = () => {
    setVisibility(false)
  }

  const handleBackdropClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    close()
  }

  const popperStyle = { zIndex: props.zIndex ?? 70 }
  const backdropStyle = {
    zIndex: props.backdropZIndex ?? Math.max((props.zIndex ?? 70) - 1, 1),
  }

  const handleDocumentClick = (event: MouseEvent) => {
    const target = event.target
    if (!(target instanceof Node)) {
      return
    }

    if (referenceRef.current?.contains(target) || popperRef.current?.contains(target)) {
      return
    }

    close()
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentClick, { passive: true })
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick)
    }
  }, [])

  useImperativeHandle(forwardedRef, () => ({
    close() {
      close()
    },
  }))

  const buttonElement =
    isValidElement<NativeTriggerButtonProps>(props.button) && typeof props.button?.type === 'string'
      ? (props.button as ReactElement<NativeTriggerButtonProps, string>)
      : null
  const isNativeButtonElement = buttonElement?.type === 'button' || buttonElement?.type === 'BUTTON'
  const innerButtonProps = isNativeButtonElement ? buttonElement.props : null
  const mergedButtonClassName = [props.btnClassName, innerButtonProps?.className]
    .filter(Boolean)
    .join(' ')

  const trigger = (
    <button
      ref={referenceRef}
      type="button"
      className={mergedButtonClassName || props.btnClassName}
      title={props.title ?? innerButtonProps?.title}
      aria-label={props.ariaLabel ?? innerButtonProps?.['aria-label']}
      onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
        innerButtonProps?.onClick?.(event)
        if (event?.defaultPrevented) return
        toggleVisibility()
      }}
      aria-expanded={visibility}
      aria-haspopup
      onKeyDown={(event) => {
        if (visibility && event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          close()
        }
      }}
    >
      {isNativeButtonElement ? (
        innerButtonProps?.children
      ) : isValidElement(props.button) ? (
        <span>{props.button}</span>
      ) : (
        props.button
      )}
    </button>
  )

  return (
    <>
      {trigger}

      {visibility && (
        <div
          className="fixed inset-0 bg-transparent"
          style={backdropStyle}
          onMouseDown={handleBackdropClick}
          onClick={handleBackdropClick}
          aria-hidden
        />
      )}

      <div
        ref={popperRef}
        style={popperStyle}
        hidden={!visibility}
        className="pointer-events-auto"
        onClick={close}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
            event.preventDefault()
            close()
          }
        }}
      >
        {visibility && props.children}
      </div>
    </>
  )
}

export default forwardRef<DropdownHandle, DropdownProps>(Dropdown)
